import { reconcileEvaluationSheetImagesAfterSave } from '../utils/evaluationSheetImageLifecycle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AutoStatusToast from '../components/common/AutoStatusToast';
import ListIcon from '../components/common/ListIcon';
import EvaluationStandardListPanel from '../components/evaluationPlan/EvaluationStandardListPanel';
import EvaluationPlanListPanel from '../components/evaluationPlan/EvaluationPlanListPanel';
import EvaluationPlanFormModal from '../components/evaluationPlan/EvaluationPlanFormModal';
import EvaluationPlanDocumentListModal from '../components/evaluationPlan/EvaluationPlanDocumentListModal';
import EvaluationStandardFormModal from '../components/evaluationPlan/EvaluationStandardFormModal';
import EvaluationStandardBulkEditModal from '../components/evaluationPlan/EvaluationStandardBulkEditModal';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { usePlatformContentEditorAccess } from '../hooks/usePlatformContentEditorAccess';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import {
  createAdminEvaluationPlanCategory,
  clearAdminEvaluationPlanSlotAttachment,
  deleteAdminEvaluationPlanCategory,
  getAdminEvaluationPlanCategories,
  getAdminEvaluationPlanCategoryBundle,
  renameAdminEvaluationPlanCategory,
  reorderAdminEvaluationPlanCategories,
  resolveAdminEvaluationPlanWorkspaceSnapshot,
  saveAdminEvaluationPlanCategoryBundle,
  updateAdminEvaluationPlanCategoryLevelStep,
} from '../services/adminEvaluationPlanService';
import type {
  EvaluationLevelStepCount,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanDraft,
  EvaluationPlanWorkspaceDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import {
  DEFAULT_LEVEL_STEP_COUNT,
  LEVEL_STEP_COUNT_OPTIONS,
  clampLevelStepCount,
  cloneEvaluationPlanDraft,
  createEmptyStandardDraft,
  formatLevelStepOptionLabel,
  normalizeEvaluationPlanWorkspaceDraft,
  planLinksStandard,
} from '../types/evaluationPlan';
import {
  loadEvaluationPlanWorkspaceSessionState,
  saveEvaluationPlanWorkspaceSessionEntry,
} from '../utils/adminEvaluationPlanCache';
import {
  buildEvaluationPlanDocumentListItems,
  type EvaluationPlanDocumentListItem,
} from '../utils/evaluationPlanDocumentList';

const SortableCategoryItem: React.FC<{
  id: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}> = ({ id, active, busy, onSelect, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.84 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-admin-learning-template-item cp-evaluation-plan-category-item${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
      aria-selected={active}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      {...attributes}
      {...listeners}
    >
      <div className="cp-admin-learning-template-tab">{id}</div>
      <div className="cp-admin-learning-template-item-actions">
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn"
          title="카테고리 이름 수정"
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
          disabled={busy}
          aria-label="카테고리 수정"
        >
          <img src="/edit.svg" alt="" className="track-icon" />
        </button>
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn cp-admin-learning-template-icon-btn-delete"
          title="카테고리 삭제"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={busy}
          aria-label="카테고리 삭제"
        >
          <img src="/Trash.svg" alt="" className="track-icon" />
        </button>
      </div>
    </div>
  );
};

function emptyDraft(categoryName: string): EvaluationPlanWorkspaceDraft {
  return {
    categoryName,
    standards: [],
    plans: [],
    deletedStandardIds: [],
    deletedPlanIds: [],
  };
}

type CategoryDraftCacheEntry = {
  draft: EvaluationPlanWorkspaceDraft;
  baselineJson: string;
};

function clearPlanSlotAttachment(plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot): EvaluationPlanDraft {
  return slot === 'criterion'
    ? { ...plan, criterion_rubric_attachment: null }
    : { ...plan, evaluation_sheet_attachment: null };
}

const AdminEvaluationPlanPage: React.FC = () => {
  const { ready: accessReady, allowed: canAccess } = usePlatformContentEditorAccess();
  const { setEvaluationPlanDirty, saveEvaluationPlanRef } = React.useContext(GradeTrackLeaveContext);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryLevelStepByName, setCategoryLevelStepByName] = useState<Record<string, EvaluationLevelStepCount>>({});
  const [categoryName, setCategoryName] = useState('');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EvaluationPlanWorkspaceDraft>(() => emptyDraft(''));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [baselineJson, setBaselineJson] = useState(() => normalizeEvaluationPlanWorkspaceDraft(emptyDraft('')));
  /** 저장 직전의 저장 상태 — 저장 후 빠진 평가지 이미지 정리용 */
  const baselineJsonForImagesRef = useRef(baselineJson);
  baselineJsonForImagesRef.current = baselineJson;
  const [selectedStandardId, setSelectedStandardId] = useState<string | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [planFilterQuery, setPlanFilterQuery] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryLevelStep, setNewCategoryLevelStep] = useState<EvaluationLevelStepCount>(DEFAULT_LEVEL_STEP_COUNT);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSourceName, setRenameSourceName] = useState('');
  const [renameDraftName, setRenameDraftName] = useState('');
  const [renameLevelStep, setRenameLevelStep] = useState<EvaluationLevelStepCount>(DEFAULT_LEVEL_STEP_COUNT);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetName, setDeleteTargetName] = useState('');
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [standardModalOpen, setStandardModalOpen] = useState(false);
  const [standardModalMode, setStandardModalMode] = useState<'create' | 'edit'>('create');
  const [standardModalInitial, setStandardModalInitial] = useState<EvaluationStandardDraft | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalMode, setPlanModalMode] = useState<'create' | 'edit'>('create');
  const [planModalInitial, setPlanModalInitial] = useState<EvaluationPlanDraft | null>(null);
  const [criterionDocListOpen, setCriterionDocListOpen] = useState(false);
  const [sheetDocListOpen, setSheetDocListOpen] = useState(false);
  const [criterionDocListItems, setCriterionDocListItems] = useState<EvaluationPlanDocumentListItem[]>([]);
  const [sheetDocListItems, setSheetDocListItems] = useState<EvaluationPlanDocumentListItem[]>([]);
  const [criterionDocListLoading, setCriterionDocListLoading] = useState(false);
  const [sheetDocListLoading, setSheetDocListLoading] = useState(false);
  const [criterionDocListError, setCriterionDocListError] = useState<string | null>(null);
  const [sheetDocListError, setSheetDocListError] = useState<string | null>(null);
  const categoryNameRef = useRef(categoryName);
  const categoryDraftCacheRef = useRef<Record<string, CategoryDraftCacheEntry>>({});
  const categoryLoadedRef = useRef<Set<string>>(new Set());
  categoryNameRef.current = categoryName;
  const { toastState: saveToastState, showSuccess: showSaveSuccess } = useAutoStatusToast(3000);

  const categoryDndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const isDirty = useMemo(
    () => normalizeEvaluationPlanWorkspaceDraft(draft) !== baselineJson,
    [draft, baselineJson]
  );

  const visiblePlans = useMemo(() => {
    if (!selectedStandardId) return draft.plans;
    return draft.plans.filter((plan) => planLinksStandard(plan, selectedStandardId));
  }, [draft.plans, selectedStandardId]);

  const currentLevelStepCount = categoryLevelStepByName[categoryName] ?? DEFAULT_LEVEL_STEP_COUNT;

  const applyDraft = useCallback((next: EvaluationPlanWorkspaceDraft) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setError(null);
    try {
      const rows = await getAdminEvaluationPlanCategories();
      const names = rows.map((row) => row.category_name.trim()).filter(Boolean);
      const levelSteps: Record<string, EvaluationLevelStepCount> = {};
      rows.forEach((row) => {
        const name = row.category_name.trim();
        if (!name) return;
        levelSteps[name] = clampLevelStepCount(row.level_step_count);
      });
      setCategoryLevelStepByName(levelSteps);
      setCategories(names);
      setCategoryName((prev) => (prev && names.includes(prev) ? prev : names[0] ?? ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리를 불러오지 못했습니다.');
      setCategories([]);
      setCategoryName('');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const patchLocalPlanAttachmentClear = useCallback(
    (planId: string, categoryNameForPlan: string, slot: EvaluationPlanAttachmentSlot) => {
      if (categoryNameForPlan === categoryNameRef.current.trim()) {
        setDraft((prev) => ({
          ...prev,
          plans: prev.plans.map((plan) => (plan.id === planId ? clearPlanSlotAttachment(plan, slot) : plan)),
        }));
      }
      const cached = categoryDraftCacheRef.current[categoryNameForPlan];
      if (cached) {
        const nextDraft: EvaluationPlanWorkspaceDraft = {
          ...cached.draft,
          plans: cached.draft.plans.map((plan) =>
            plan.id === planId ? clearPlanSlotAttachment(plan, slot) : plan
          ),
        };
        categoryDraftCacheRef.current[categoryNameForPlan] = {
          draft: nextDraft,
          baselineJson: cached.baselineJson,
        };
        saveEvaluationPlanWorkspaceSessionEntry(categoryNameForPlan, {
          draft: nextDraft,
          baselineJson: cached.baselineJson,
        });
      }
    },
    []
  );

  const loadDocumentList = useCallback(
    async (slot: EvaluationPlanAttachmentSlot) => {
      const setLoading = slot === 'criterion' ? setCriterionDocListLoading : setSheetDocListLoading;
      const setError = slot === 'criterion' ? setCriterionDocListError : setSheetDocListError;
      const setItems = slot === 'criterion' ? setCriterionDocListItems : setSheetDocListItems;
      setLoading(true);
      setError(null);
      try {
        const session = loadEvaluationPlanWorkspaceSessionState();
        const draftsByCategory: Record<string, EvaluationPlanWorkspaceDraft | undefined> = {};
        categories.forEach((name) => {
          const entry = session?.draftsByCategory[name] ?? categoryDraftCacheRef.current[name] ?? null;
          if (entry?.draft) draftsByCategory[name] = entry.draft;
        });

        const snapshot = await resolveAdminEvaluationPlanWorkspaceSnapshot({
          categoryNames: categories,
          draftsByCategory,
        });

        setItems(
          buildEvaluationPlanDocumentListItems({
            slot,
            plans: snapshot.plans,
            standards: snapshot.standards,
            categoryOrder: categories,
          })
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : '문서 목록을 불러오지 못했습니다.');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [categories]
  );

  const handleDeleteDocumentListItem = useCallback(
    async (slot: EvaluationPlanAttachmentSlot, listKey: string) => {
      const items = slot === 'criterion' ? criterionDocListItems : sheetDocListItems;
      const item = items.find((row) => row.listKey === listKey);
      if (!item) return false;
      await clearAdminEvaluationPlanSlotAttachment({
        planId: item.planId,
        categoryName: item.categoryName,
        slot,
      });
      patchLocalPlanAttachmentClear(item.planId, item.categoryName, slot);
      await loadDocumentList(slot);
      return true;
    },
    [criterionDocListItems, loadDocumentList, patchLocalPlanAttachmentClear, sheetDocListItems]
  );

  useEffect(() => {
    if (!criterionDocListOpen) return;
    void loadDocumentList('criterion');
  }, [criterionDocListOpen, loadDocumentList]);

  useEffect(() => {
    if (!sheetDocListOpen) return;
    void loadDocumentList('sheet');
  }, [sheetDocListOpen, loadDocumentList]);

  const loadBundle = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      const empty = emptyDraft('');
      setDraft(empty);
      setBaselineJson(normalizeEvaluationPlanWorkspaceDraft(empty));
      setSelectedStandardId(null);
      return;
    }
    setBundleLoading(true);
    setError(null);
    try {
      const bundle = await getAdminEvaluationPlanCategoryBundle(trimmed);
      const next: EvaluationPlanWorkspaceDraft = {
        categoryName: trimmed,
        standards: bundle.standards,
        plans: bundle.plans,
        deletedStandardIds: [],
        deletedPlanIds: [],
      };
      const nextBaseline = normalizeEvaluationPlanWorkspaceDraft(next);
      setDraft(next);
      setBaselineJson(nextBaseline);
      categoryLoadedRef.current.add(trimmed);
      categoryDraftCacheRef.current[trimmed] = { draft: next, baselineJson: nextBaseline };
      saveEvaluationPlanWorkspaceSessionEntry(trimmed, { draft: next, baselineJson: nextBaseline });
      setSelectedStandardId((prev) =>
        prev && bundle.standards.some((row) => row.id === prev) ? prev : null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '데이터를 불러오지 못했습니다.');
      const empty = emptyDraft(trimmed);
      const emptyBaseline = normalizeEvaluationPlanWorkspaceDraft(empty);
      setDraft(empty);
      setBaselineJson(emptyBaseline);
      categoryLoadedRef.current.add(trimmed);
      categoryDraftCacheRef.current[trimmed] = { draft: empty, baselineJson: emptyBaseline };
      saveEvaluationPlanWorkspaceSessionEntry(trimmed, { draft: empty, baselineJson: emptyBaseline });
      setSelectedStandardId(null);
    } finally {
      setBundleLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!categoryName.trim()) {
      const empty = emptyDraft('');
      setDraft(empty);
      setBaselineJson(normalizeEvaluationPlanWorkspaceDraft(empty));
      setSelectedStandardId(null);
      setFilterQuery('');
      setPlanFilterQuery('');
      return;
    }

    const sessionEntry = loadEvaluationPlanWorkspaceSessionState()?.draftsByCategory[categoryName];
    if (sessionEntry) {
      setDraft(JSON.parse(JSON.stringify(sessionEntry.draft)) as EvaluationPlanWorkspaceDraft);
      setBaselineJson(sessionEntry.baselineJson);
      categoryLoadedRef.current.add(categoryName);
      categoryDraftCacheRef.current[categoryName] = {
        draft: JSON.parse(JSON.stringify(sessionEntry.draft)) as EvaluationPlanWorkspaceDraft,
        baselineJson: sessionEntry.baselineJson,
      };
      setSelectedStandardId((prev) =>
        prev && sessionEntry.draft.standards.some((row) => row.id === prev) ? prev : null
      );
      setFilterQuery('');
      setPlanFilterQuery('');
      return;
    }

    const cached = categoryDraftCacheRef.current[categoryName];
    if (cached && categoryLoadedRef.current.has(categoryName)) {
      setDraft(JSON.parse(JSON.stringify(cached.draft)) as EvaluationPlanWorkspaceDraft);
      setBaselineJson(cached.baselineJson);
      setSelectedStandardId((prev) =>
        prev && cached.draft.standards.some((row) => row.id === prev) ? prev : null
      );
      setFilterQuery('');
      setPlanFilterQuery('');
      return;
    }

    void loadBundle(categoryName);
    setFilterQuery('');
    setPlanFilterQuery('');
  }, [categoryName, loadBundle]);

  useEffect(() => {
    const trimmed = categoryName.trim();
    if (!trimmed || !categoryLoadedRef.current.has(trimmed)) return;
    const entry = {
      draft: JSON.parse(JSON.stringify(draft)) as EvaluationPlanWorkspaceDraft,
      baselineJson,
    };
    categoryDraftCacheRef.current[trimmed] = entry;
    saveEvaluationPlanWorkspaceSessionEntry(trimmed, entry);
  }, [categoryName, draft, baselineJson]);

  useEffect(() => {
    setPlanFilterQuery('');
  }, [selectedStandardId]);

  useEffect(() => {
    setEvaluationPlanDirty(isDirty);
  }, [isDirty, setEvaluationPlanDirty]);

  const handleSave = useCallback(async () => {
    const current = draftRef.current;
    if (!current.categoryName.trim()) return;
    setSaving(true);
    setError(null);
    const prevSavedForImages = baselineJsonForImagesRef.current;
    try {
      const saved = await saveAdminEvaluationPlanCategoryBundle(current);
      const next: EvaluationPlanWorkspaceDraft = {
        categoryName: saved.categoryName,
        standards: saved.standards,
        plans: saved.plans,
        deletedStandardIds: [],
        deletedPlanIds: [],
      };
      const nextBaseline = normalizeEvaluationPlanWorkspaceDraft(next);
      draftRef.current = next;
      setDraft(next);
      setBaselineJson(nextBaseline);
      // 저장 성공 후: 수정·삭제로 빠진 평가지 이미지 → 서버 참조 확인 후 삭제
      reconcileEvaluationSheetImagesAfterSave(prevSavedForImages, next);
      categoryLoadedRef.current.add(saved.categoryName);
      categoryDraftCacheRef.current[saved.categoryName] = { draft: next, baselineJson: nextBaseline };
      saveEvaluationPlanWorkspaceSessionEntry(saved.categoryName, { draft: next, baselineJson: nextBaseline });
      setSelectedStandardId((prev) =>
        prev && saved.standards.some((row) => row.id === prev) ? prev : null
      );
      showSaveSuccess('평가계획 데이터가 저장되었습니다.');
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [showSaveSuccess]);

  const applyDraftAndSave = useCallback(
    (next: EvaluationPlanWorkspaceDraft) => {
      applyDraft(next);
      void handleSave();
    },
    [applyDraft, handleSave]
  );

  const handleDiscard = useCallback(() => {
    setDraft(JSON.parse(baselineJson) as EvaluationPlanWorkspaceDraft);
    setSelectedStandardId((prev) => {
      const parsed = JSON.parse(baselineJson) as EvaluationPlanWorkspaceDraft;
      return prev && parsed.standards.some((row) => row.id === prev) ? prev : null;
    });
  }, [baselineJson]);

  useEffect(() => {
    saveEvaluationPlanRef.current = { save: handleSave, discard: handleDiscard };
    return () => {
      saveEvaluationPlanRef.current = null;
    };
  }, [handleSave, handleDiscard, saveEvaluationPlanRef]);

  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.indexOf(String(active.id));
    const newIndex = categories.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(categories, oldIndex, newIndex);
    setCategories(next);
    setCategoryBusy(true);
    try {
      await reorderAdminEvaluationPlanCategories(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리 순서 변경에 실패했습니다.');
      void loadCategories();
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setCategoryBusy(true);
    try {
      await createAdminEvaluationPlanCategory(name, newCategoryLevelStep);
      setCategoryModalOpen(false);
      setNewCategoryName('');
      setNewCategoryLevelStep(DEFAULT_LEVEL_STEP_COUNT);
      await loadCategories();
      setCategoryName(name);
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리 추가에 실패했습니다.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleRenameCategory = async () => {
    const from = renameSourceName.trim();
    const to = renameDraftName.trim();
    if (!from || !to) return;
    setCategoryBusy(true);
    try {
      if (from !== to) {
        await renameAdminEvaluationPlanCategory(from, to);
      }
      const previousLevelStep = categoryLevelStepByName[from] ?? DEFAULT_LEVEL_STEP_COUNT;
      const targetName = from !== to ? to : from;
      if (renameLevelStep !== previousLevelStep) {
        await updateAdminEvaluationPlanCategoryLevelStep(targetName, renameLevelStep);
      }
      setRenameModalOpen(false);
      await loadCategories();
      if (categoryNameRef.current === from) setCategoryName(to);
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리 설정 변경에 실패했습니다.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleDeleteCategory = async () => {
    const name = deleteTargetName.trim();
    if (!name) return;
    setCategoryBusy(true);
    try {
      await deleteAdminEvaluationPlanCategory(name);
      setDeleteModalOpen(false);
      await loadCategories();
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리 삭제에 실패했습니다.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleStandardSubmit = (standard: EvaluationStandardDraft) => {
    applyDraftAndSave({
      ...draftRef.current,
      standards: standardModalMode === 'create'
        ? [...draftRef.current.standards, { ...standard, sort_order: draftRef.current.standards.length }]
        : draftRef.current.standards.map((row) => (row.id === standard.id ? standard : row)),
    });
    if (standardModalMode === 'create') setSelectedStandardId(null);
  };

  const handleToggleStandardFilter = (standardId: string) => {
    setSelectedStandardId((prev) => (prev === standardId ? null : standardId));
  };

  const handleDeleteStandard = (standardId: string) => {
    applyDraft({
      ...draft,
      standards: draft.standards.filter((row) => row.id !== standardId),
      plans: draft.plans.map((plan) => ({
        ...plan,
        standard_ids: (plan.standard_ids ?? []).filter((id) => id !== standardId),
      })),
      deletedStandardIds: [...draft.deletedStandardIds, standardId],
    });
    if (selectedStandardId === standardId) {
      setSelectedStandardId(null);
    }
  };

  const handleBulkApply = (standards: EvaluationStandardDraft[]) => {
    const incomingIds = new Set(standards.map((row) => row.id));
    const current = draftRef.current;
    const removed = current.standards.filter((row) => !incomingIds.has(row.id));
    const orderedStandards = standards.map((row, idx) => ({
      ...row,
      sort_order: idx,
      category_name: categoryName,
    }));
    applyDraftAndSave({
      ...current,
      standards: orderedStandards,
      plans: current.plans.map((plan) => ({
        ...plan,
        standard_ids: (plan.standard_ids ?? []).filter((id) => incomingIds.has(id)),
      })),
      deletedStandardIds: [...current.deletedStandardIds, ...removed.map((row) => row.id)],
    });
    if (selectedStandardId && !incomingIds.has(selectedStandardId)) {
      setSelectedStandardId(null);
    }
  };

  const handleAddPlan = () => {
    setPlanModalMode('create');
    setPlanModalInitial(null);
    setPlanModalOpen(true);
  };

  const handleEditPlan = (plan: EvaluationPlanDraft) => {
    setPlanModalMode('edit');
    setPlanModalInitial(cloneEvaluationPlanDraft(plan));
    setPlanModalOpen(true);
  };

  const handlePlanDraftChange = (plan: EvaluationPlanDraft) => {
    const cloned = cloneEvaluationPlanDraft(plan);
    applyDraft({
      ...draftRef.current,
      plans: draftRef.current.plans.map((row) => (row.id === cloned.id ? cloned : row)),
    });
    setPlanModalInitial((prev) => (prev?.id === cloned.id ? cloned : prev));
  };

  const handlePlanSubmit = (plan: EvaluationPlanDraft) => {
    const current = draftRef.current;
    if (planModalMode === 'create') {
      applyDraftAndSave({ ...current, plans: [...current.plans, plan] });
      return;
    }
    applyDraftAndSave({
      ...current,
      plans: current.plans.map((row) => (row.id === plan.id ? plan : row)),
    });
  };

  const handleDeletePlan = (planId: string) => {
    applyDraft({
      ...draft,
      plans: draft.plans.filter((row) => row.id !== planId),
      deletedPlanIds: [...draft.deletedPlanIds, planId],
    });
  };

  const trySelectCategory = (name: string) => {
    if (name === categoryName) return;
    if (isDirty) {
      const ok = window.confirm('저장하지 않은 변경이 있습니다. 카테고리를 변경하시겠습니까?');
      if (!ok) return;
    }
    setPlanModalOpen(false);
    setStandardModalOpen(false);
    setBulkModalOpen(false);
    setCategoryName(name);
  };

  if (!accessReady) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-evaluation-plan-title">
        <h2 id="cp-evaluation-plan-title" className="cp-page-title">
          평가계획 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="status">
          확인 중…
        </p>
      </section>
    );
  }

  if (!canAccess) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-evaluation-plan-title">
        <h2 id="cp-evaluation-plan-title" className="cp-page-title">
          평가계획 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자 또는 매니저만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section className="cp-admin-notices-page cp-admin-notices-page--edu cp-evaluation-plan-page cp-evaluation-plan-page--edu" aria-labelledby="cp-evaluation-plan-title">
      <div className="cp-page-title-row cp-admin-notices-page-title-row cp-evaluation-plan-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-evaluation-plan-title">평가계획 관리</h2>
          <p className="cp-admin-legal-subtitle">
            플랫폼 전역 평가계획입니다. 카테고리별 성취기준과 평가계획을 등록·관리할 수 있습니다.
          </p>
        </div>
        <div className="cp-annual-top-actions-save cp-evaluation-plan-page-save cp-page-save-actions--chunky" role="group" aria-label="편집 저장 도구">
          <ProgramToolbarUndoRedoButtons
            canUndo={false}
            canRedo={false}
            onUndo={() => {}}
            onRedo={() => {}}
            disabled
          />
          <button
            type="button"
            className="cp-btn-save-all cp-admin-learning-top-save-btn"
            onClick={() => void handleSave()}
            disabled={!isDirty || saving || !categoryName}
            title={isDirty && categoryName ? '저장하기' : '저장할 변경사항 없음'}
          >
            {saving ? '저장 중…' : isDirty && categoryName ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      <div className="cp-evaluation-plan-category-tabs-row">
        <button
          type="button"
          className="cp-btn-copy-paste cp-admin-learning-add-template-btn cp-evaluation-plan-category-add-btn"
          onClick={() => {
            setNewCategoryLevelStep(DEFAULT_LEVEL_STEP_COUNT);
            setCategoryModalOpen(true);
          }}
          disabled={categoryBusy}
          aria-label="카테고리 추가"
        >
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--full">카테고리 추가하기 +</span>
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--short">카테고리 +</span>
        </button>
        <div className="cp-admin-legal-subtabs cp-evaluation-plan-category-tabs" role="tablist" aria-label="카테고리">
          <DndContext sensors={categoryDndSensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleCategoryDragEnd(e)}>
            <SortableContext items={categories} strategy={horizontalListSortingStrategy}>
              {categories.map((name) => (
                <SortableCategoryItem
                  key={name}
                  id={name}
                  active={categoryName === name}
                  busy={categoryBusy}
                  onSelect={() => trySelectCategory(name)}
                  onRename={() => {
                    setRenameSourceName(name);
                    setRenameDraftName(name);
                    setRenameLevelStep(categoryLevelStepByName[name] ?? DEFAULT_LEVEL_STEP_COUNT);
                    setRenameModalOpen(true);
                  }}
                  onDelete={() => {
                    setDeleteTargetName(name);
                    setDeleteModalOpen(true);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="cp-evaluation-plan-category-doc-list-actions">
          <button
            type="button"
            className="cp-evaluation-plan-section-head-btn cp-evaluation-plan-section-head-btn--with-icon"
            onClick={() => setCriterionDocListOpen(true)}
            disabled={categoriesLoading || categoryBusy}
          >
            <ListIcon />
            기준안 문서 목록
          </button>
          <button
            type="button"
            className="cp-evaluation-plan-section-head-btn cp-evaluation-plan-section-head-btn--with-icon"
            onClick={() => setSheetDocListOpen(true)}
            disabled={categoriesLoading || categoryBusy}
          >
            <ListIcon />
            평가지 문서 목록
          </button>
        </div>
      </div>

      {error ? <p className="cp-admin-learning-error">{error}</p> : null}

      {categoriesLoading ? (
        <p className="cp-dashboard-placeholder" role="status">
          카테고리를 불러오는 중…
        </p>
      ) : !categoryName ? (
        <p className="cp-dashboard-placeholder">카테고리를 먼저 추가해 주세요.</p>
      ) : bundleLoading ? (
        <p className="cp-dashboard-placeholder" role="status">
          데이터를 불러오는 중…
        </p>
      ) : (
        <div className="cp-admin-notices-grid cp-evaluation-plan-grid" role="presentation">
          <EvaluationStandardListPanel
            standards={draft.standards}
            selectedStandardId={selectedStandardId}
            filterQuery={filterQuery}
            onFilterQueryChange={setFilterQuery}
            onViewAll={() => setFilterQuery('')}
            onSelectStandard={handleToggleStandardFilter}
            onAddStandard={() => {
              setStandardModalMode('create');
              setStandardModalInitial(
                createEmptyStandardDraft(categoryName, draft.standards.length)
              );
              setStandardModalOpen(true);
            }}
            onEditStandard={(standard) => {
              setStandardModalMode('edit');
              setStandardModalInitial(standard);
              setStandardModalOpen(true);
            }}
            onDeleteStandard={handleDeleteStandard}
            onOpenBulkEdit={() => setBulkModalOpen(true)}
          />
          <EvaluationPlanListPanel
            standards={draft.standards}
            plans={visiblePlans}
            allPlans={draft.plans}
            filterQuery={planFilterQuery}
            filterStandardId={selectedStandardId}
            onFilterQueryChange={setPlanFilterQuery}
            onViewAll={() => {
              setPlanFilterQuery('');
              setSelectedStandardId(null);
            }}
            onAddPlan={handleAddPlan}
            onEditPlan={handleEditPlan}
            onDeletePlan={handleDeletePlan}
          />
        </div>
      )}

      <EvaluationStandardFormModal
        open={standardModalOpen}
        mode={standardModalMode}
        initial={standardModalInitial}
        categoryName={categoryName}
        levelStepCount={currentLevelStepCount}
        onClose={() => setStandardModalOpen(false)}
        onSubmit={handleStandardSubmit}
      />

      <EvaluationStandardBulkEditModal
        open={bulkModalOpen}
        categoryName={categoryName}
        standards={draft.standards}
        levelStepCount={currentLevelStepCount}
        onClose={() => setBulkModalOpen(false)}
        onApply={handleBulkApply}
      />

      <EvaluationPlanFormModal
        open={planModalOpen}
        mode={planModalMode}
        categoryName={categoryName}
        standards={draft.standards}
        initial={planModalInitial}
        initialStandardIds={selectedStandardId ? [selectedStandardId] : []}
        sortOrder={draft.plans.length}
        defaultLevelStepCount={currentLevelStepCount}
        onClose={() => setPlanModalOpen(false)}
        onSubmit={handlePlanSubmit}
        onDraftChange={planModalMode === 'edit' ? handlePlanDraftChange : undefined}
      />

      {categoryModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setCategoryModalOpen(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>카테고리 추가</h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>카테고리 이름</span>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="cp-evaluation-plan-field">
                <span>성취수준 단계</span>
                <select
                  value={newCategoryLevelStep}
                  onChange={(e) => setNewCategoryLevelStep(clampLevelStepCount(Number(e.target.value)))}
                  aria-label="성취수준 단계"
                >
                  {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                    <option key={count} value={count}>
                      {formatLevelStepOptionLabel(count)}
                    </option>
                  ))}
                </select>
              </label>
              <p className="cp-evaluation-plan-level-settings-hint" role="status">
                이 단계는 해당 카테고리에서 평가계획을 만들 때 기본값으로 적용됩니다. 각 평가계획에서 바꿀 수 있습니다.
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setCategoryModalOpen(false)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" disabled={categoryBusy} onClick={() => void handleCreateCategory()}>
                추가
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renameModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setRenameModalOpen(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>카테고리 설정</h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>카테고리 이름</span>
                <input
                  type="text"
                  value={renameDraftName}
                  onChange={(e) => setRenameDraftName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="cp-evaluation-plan-field">
                <span>성취수준 단계</span>
                <select
                  value={renameLevelStep}
                  onChange={(e) => setRenameLevelStep(clampLevelStepCount(Number(e.target.value)))}
                  aria-label="성취수준 단계"
                >
                  {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                    <option key={count} value={count}>
                      {formatLevelStepOptionLabel(count)}
                    </option>
                  ))}
                </select>
              </label>
              <p className="cp-evaluation-plan-level-settings-hint" role="status">
                이 단계는 해당 카테고리에서 평가계획을 만들 때 기본값으로 적용됩니다. 각 평가계획에서 바꿀 수 있습니다.
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setRenameModalOpen(false)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" disabled={categoryBusy} onClick={() => void handleRenameCategory()}>
                저장
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setDeleteModalOpen(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>카테고리 삭제</h3>
            <div className="cp-modal-body">
              <p>
                「{deleteTargetName}」 카테고리와 포함된 성취기준·평가계획이 모두 삭제됩니다. 계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteModalOpen(false)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" disabled={categoryBusy} onClick={() => void handleDeleteCategory()}>
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <EvaluationPlanDocumentListModal
        open={criterionDocListOpen}
        onClose={() => setCriterionDocListOpen(false)}
        slot="criterion"
        items={criterionDocListItems}
        isLoading={criterionDocListLoading}
        loadError={criterionDocListError}
        actionsDisabled={saving || categoryBusy}
        onDeleteItem={(listKey) => handleDeleteDocumentListItem('criterion', listKey)}
        onNotify={showSaveSuccess}
      />

      <EvaluationPlanDocumentListModal
        open={sheetDocListOpen}
        onClose={() => setSheetDocListOpen(false)}
        slot="sheet"
        items={sheetDocListItems}
        isLoading={sheetDocListLoading}
        loadError={sheetDocListError}
        actionsDisabled={saving || categoryBusy}
        onDeleteItem={(listKey) => handleDeleteDocumentListItem('sheet', listKey)}
        onNotify={showSaveSuccess}
      />

      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSave()}
        disabled={!isDirty || saving || !categoryName}
        aria-label="저장하기"
        title={isDirty && categoryName ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default AdminEvaluationPlanPage;
